/**
 * SubjectSuperCurricularMapModal.jsx
 *
 * Node-link diagram of supercurricular activities for a given subject,
 * grouped by category with distinct colours:
 *   - Intellectual Pursuits  → amber   #ffaa00
 *   - Clubs and Societies    → purple  #b794f4
 *   - Work Experience        → teal    #00e5c8
 *
 * Architecture mirrors SubjectCareerMapModal but adds:
 *   1. A category ring between the centre and leaf nodes
 *   2. Two-level links:  centre → category hub → activity leaf
 *   3. A legend at the bottom
 */

import { useEffect, useRef, useState, useMemo } from "react";
import { FaBolt } from "react-icons/fa6";

/* ─── Easing ──────────────────────────────────────────────────── */
function easeOutBack(t) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

/* ─── Category palette ────────────────────────────────────────── */
const CATEGORY_CONFIG = {
  "Intellectual Pursuits": {
    color:  "#ffaa00",
    glow:   "rgba(255,170,0,0.55)",
    fill:   "rgba(255,170,0,0.18)",
    stroke: "#ffaa00",
  },
  "Clubs and Societies": {
    color:  "#b794f4",
    glow:   "rgba(183,148,244,0.55)",
    fill:   "rgba(183,148,244,0.18)",
    stroke: "#b794f4",
  },
  "Work Experience": {
    color:  "#00e5c8",
    glow:   "rgba(0,229,200,0.55)",
    fill:   "rgba(0,229,200,0.18)",
    stroke: "#00e5c8",
  },
};

const DEFAULT_CAT = {
  color:  "#36a7f8",
  glow:   "rgba(54,167,248,0.55)",
  fill:   "rgba(54,167,248,0.18)",
  stroke: "#36a7f8",
};

function getCat(name) {
  return CATEGORY_CONFIG[name] || DEFAULT_CAT;
}

/* ─── Layout constants ────────────────────────────────────────── */
const W  = 1400;
const H  = 1450;
const CX = W / 2;
const CY = H / 2;

const CENTER_R    = 88;   // subject hub
const CAT_HUB_R   = 70;   // category hub
const LEAF_R      = 80;   // activity leaf
const CAT_ORBIT   = 200;  // distance centre → category hub
const LEAF_ORBIT  = 300;  // distance category hub → leaf
const FOCUS_LEAF_ORBIT_INNER = 360;
const FOCUS_LEAF_ORBIT_OUTER = 600;

/* ─── Text wrap helper ────────────────────────────────────────── */
function wrapWords(text, maxChars = 13) {
  const words = (text || "").split(" ");
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length <= maxChars) {
      cur = (cur + " " + w).trim();
    } else {
      if (cur) lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines.slice(0, 3);
}

/* ─── Position helpers ────────────────────────────────────────── */
function polarToCart(cx, cy, r, angleRad) {
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  };
}

/**
 * Given a list of activities, compute:
 *   - positions for each category hub
 *   - positions for each activity leaf around its hub
 */
function computeLayout(activities) {
  const groups = {};
  activities.forEach(a => {
    const cat = a.category || "Other";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(a);
  });

  const catNames = Object.keys(groups);
  const numCats  = catNames.length;

  const catNodes  = [];
  const leafNodes = [];

  catNames.forEach((cat, ci) => {
    const catAngle = (2 * Math.PI * ci) / numCats - Math.PI / 2;
    const hub = polarToCart(CX, CY, CAT_ORBIT, catAngle);

    catNodes.push({ cat, angle: catAngle, ...hub });

    const acts = groups[cat];
    const numActs = acts.length;

    const spreadAngle = Math.min(Math.PI * 1.1, (numActs - 1) * 0.55 + 0.3);
    const startAngle  = catAngle - spreadAngle / 2;

    // Category-level base variation
    const catBaseOffset = (ci % 3 - 1) * 1; // subtle per-category shift

    acts.forEach((act, ai) => {
      const leafAngle = numActs === 1
        ? catAngle
        : startAngle + (spreadAngle * ai) / (numActs - 1);

      // === ENHANCED VARIABLE RADIUS ===
      const angleFactor = Math.sin(ai * 2.7) * 0.6;           // organic spread
      const randomFactor = (Math.sin(ai * 7.3) * 0.4 + 0.6); // pseudo-random
      const sizeFactor = Math.min(2.5, 0.7 + acts.length * 1); // more spread for crowded categories

      const radiusJitter = 
        angleFactor * 55 +
        randomFactor * 48 +
        catBaseOffset;

      const finalRadius = LEAF_ORBIT + radiusJitter * sizeFactor;

      const pos = polarToCart(hub.x, hub.y, finalRadius, leafAngle);

      leafNodes.push({ 
        act, 
        cat, 
        leafAngle, 
        radius: finalRadius,   // optional: can be used later
        ...pos 
      });
    });
  });

  return { catNodes, leafNodes, groups };
}

/**
 * Lays out a single category's activities in a full ring around the
 * diagram centre. Used in "focus mode" once overlap-causing categories
 * have been hidden, so this category can claim the freed-up space.
 * Alternates two radii so evenly-spaced angles don't still collide when
 * there are a lot of activities.
 */
function computeFocusLayout(acts) {
  const numActs = acts.length;
  return acts.map((act, ai) => {
    const angle  = (2 * Math.PI * ai) / numActs - Math.PI / 2;
    const radius = ai % 2 === 0 ? FOCUS_LEAF_ORBIT_INNER : FOCUS_LEAF_ORBIT_OUTER;
    const pos    = polarToCart(CX, CY, radius, angle);
    return { act, angle, radius, ...pos };
  });
}

/* ═══════════════════════════════════════════════════════════════ */
export default function SubjectSuperCurricularMapModal({
  subject,
  activities = [],   // flat array of supercurricular activity objects
  loading    = false,
  onClose,
  onActivityClick,  // (activity) => void
}) {
  const [hovered,    setHovered]    = useState(null);   // leaf supercurricular_id
  const [hovCat,     setHovCat]     = useState(null);   // category hub name
  const [progress,   setProgress]   = useState(0);
  const [focusedCat, setFocusedCat] = useState(null);    // category currently drilled into, or null for overview

  const rafRef   = useRef(null);
  const startRef = useRef(null);

  /* ── Entrance animation (also replays on focus/unfocus for a "zoom" feel) ── */
  useEffect(() => {
    startRef.current = performance.now();
    const DURATION = 550;
    setProgress(0);
    const tick = (now) => {
      const t = Math.min((now - startRef.current) / DURATION, 1);
      setProgress(easeOutBack(t));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [focusedCat]);

  /* ── Keyboard: Escape steps back out of focus first, then closes ── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (focusedCat) setFocusedCat(null);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusedCat, onClose]);

  /* ── Layout (memoised) ── */
  const { catNodes, leafNodes, groups } = useMemo(
    () => computeLayout(activities),
    [activities]
  );

  /* ── Focus-mode layout: only computed when a category is drilled into ── */
  const focusLeafNodes = useMemo(
    () => focusedCat ? computeFocusLayout(groups[focusedCat] || []) : [],
    [focusedCat, groups]
  );

  /* ── Animated position helper ── */
  const animPos = (finalX, finalY, originX = CX, originY = CY) => ({
    x: originX + (finalX - originX) * progress,
    y: originY + (finalY - originY) * progress,
  });

  const catNames = Object.keys(groups);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.82)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 12000, backdropFilter: "blur(6px)",
      }}
    >
      <div style={{
        background: "rgba(6,10,26,0.97)",
        borderRadius: 24,
        boxShadow: "0 40px 120px rgba(0,0,0,0.85), 0 0 100px rgba(255,170,0,0.12)",
        padding: "18px 32px 12px",
        maxWidth: "min(96vw, 1380px)",
        width: "100%",
        position: "relative",
        background: "rgba(3, 0, 25, 0)",
      }}>
        {/* ── Header ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
          <div>
              <FaBolt 
                size={15} 
                color="#ffaa00" 
                style={{ marginRight: "10px", verticalAlign: "middle" }}
              />
            <span style={{ margin: 0, fontSize: "0.82rem", color: "#ffaa00", letterSpacing: "0.12em", fontWeight: 700 }}>
              SuperCurricular
            </span>
            <h3 style={{ margin: "4px 0 0", color: "#ffffff", fontSize: "1.55rem", fontWeight: 700 }}>
              {subject}
            </h3>
          </div>
                 <button onClick={onClose} style={{ background: "none", border: "none", color: "#88a", fontSize: "2.2rem", cursor: "pointer", padding: "0 6px" }}>
            ×
          </button>
        </div>

        {/* ── Category tabs: quick way to focus/switch/return to overview ── */}
        {!loading && catNames.length > 1 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            <button
              onClick={() => setFocusedCat(null)}
              style={{
                padding: "5px 14px", borderRadius: 999, cursor: "pointer",
                fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.02em",
                border: focusedCat === null ? "1.5px solid #fff" : "1.5px solid #ffffff33",
                background: focusedCat === null ? "#ffffff1a" : "transparent",
                color: focusedCat === null ? "#fff" : "#aab",
              }}
            >
              All ({activities.length})
            </button>
            {catNames.map(cat => {
              const cfg = getCat(cat);
              const active = focusedCat === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setFocusedCat(active ? null : cat)}
                  style={{
                    padding: "5px 14px", borderRadius: 999, cursor: "pointer",
                    fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.02em",
                    border: `1.5px solid ${active ? cfg.color : cfg.color + "55"}`,
                    background: active ? cfg.fill : "transparent",
                    color: active ? cfg.color : "#aab",
                  }}
                >
                  {cat} ({(groups[cat] || []).length})
                </button>
              );
            })}
          </div>
        )}

        {/* ── SVG diagram ── */}
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", maxHeight: "86vh" }}>
          <defs>
            {/* Glow filter per category */}
            {catNames.map(cat => {
              const cfg = getCat(cat);
              return (
                <filter key={`glow-${cat}`} id={`glow-${cat.replace(/\s+/g,"-")}`} x="-80%" y="-80%" width="260%" height="260%">
                  <feGaussianBlur stdDeviation="10" result="blur"/>
                  <feFlood floodColor={cfg.color} floodOpacity="0.7" result="col"/>
                  <feComposite in="col" in2="blur" operator="in" result="glow"/>
                  <feMerge>
                    <feMergeNode in="glow"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              );
            })}

            {/* Centre glow */}
            <filter id="glow-centre" x="-90%" y="-90%" width="280%" height="280%">
              <feGaussianBlur stdDeviation="14" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>

            {/* 3-D shadow */}
            <filter id="shadow3d" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="7"/>
              <feOffset dx="5" dy="8" result="off"/>
              <feComponentTransfer><feFuncA type="linear" slope="0.55"/></feComponentTransfer>
              <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>

            {/* Centre radial gradient */}
            <radialGradient id="centreGrad" cx="35%" cy="32%" r="68%">
              <stop offset="0%"   stopColor="#ffe58870"/>
              <stop offset="50%"  stopColor="#ffaa0060"/>
              <stop offset="100%" stopColor="#d46a0050"/>
            </radialGradient>
          </defs>

            {/* ── Spokes: centre → category hubs (overview mode only) ── */}
          {!loading && !focusedCat && catNodes.map(({ cat, x, y }) => {
            const p = animPos(x, y);
            const isHov = hovCat === cat;
            const cfg = getCat(cat);
            return (
              <line key={`spoke-${cat}`}
                x1={CX} y1={CY} x2={p.x} y2={p.y}
                stroke={isHov ? cfg.color : cfg.color + "55"}
                strokeWidth={isHov ? 3.5 : 1.8}
                strokeDasharray={isHov ? "none" : "6 4"}
              />
            );
          })}

                   {/* ── Spokes: category hub → activity leaves (overview mode only) ── */}
          {!loading && !focusedCat && leafNodes.map(({ act, cat, x, y }) => {
            const catHub = catNodes.find(c => c.cat === cat);
            if (!catHub) return null;
            const catP  = animPos(catHub.x, catHub.y);
            const leafP = animPos(x, y, catHub.x, catHub.y);
            const isHov = hovered === act.supercurricular_id;
            const cfg   = getCat(cat);
            return (
              <line key={`branch-${act.supercurricular_id}`}
                x1={catP.x} y1={catP.y} x2={leafP.x} y2={leafP.y}
                stroke={isHov ? cfg.color : cfg.color + "66"}
                strokeWidth={isHov ? 3 : 1.8}
                strokeLinecap="round"
              />
            );
          })}

          {/* ── Spokes: centre → leaves (focus mode only) ── */}
          {!loading && focusedCat && focusLeafNodes.map(({ act, x, y }) => {
            const p     = animPos(x, y, CX, CY);
            const isHov = hovered === act.supercurricular_id;
            const cfg   = getCat(focusedCat);
            return (
              <line key={`fbranch-${act.supercurricular_id}`}
                x1={CX} y1={CY} x2={p.x} y2={p.y}
                stroke={isHov ? cfg.color : cfg.color + "66"}
                strokeWidth={isHov ? 3 : 1.8}
                strokeLinecap="round"
              />
            );
          })}

                    {/* ── Activity leaf nodes (overview OR focus mode, same render body) ── */}
          {!loading && (focusedCat
            ? focusLeafNodes.map(n => ({ ...n, cat: focusedCat }))
            : leafNodes
          ).map(({ act, cat, x, y }) => {
            const catHub = focusedCat ? null : catNodes.find(c => c.cat === cat);
            const p    = animPos(x, y, catHub ? catHub.x : CX, catHub ? catHub.y : CY);
            const isHov = hovered === act.supercurricular_id;
            const cfg  = getCat(cat);
            const filtId = `glow-${cat.replace(/\s+/g,"-")}`;
            const lines  = wrapWords(act.activity_title || act.title || "", 13);
            const lineH  = 14;
            const textY  = p.y - ((lines.length - 1) * lineH) / 2 + 1;

            return (
              <g key={`leaf-${act.supercurricular_id}`}
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHovered(act.supercurricular_id)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => { onActivityClick(act); onClose(); }}
              >
                {/* Shadow */}
                <circle cx={p.x + 5} cy={p.y + 7} r={LEAF_R}
                  fill="#00000055" filter="url(#shadow3d)" />

                {/* Body */}
                <circle cx={p.x} cy={p.y} r={LEAF_R}
                  fill={cfg.fill}
                  stroke={isHov ? cfg.color : cfg.stroke + "cc"}
                  strokeWidth={isHov ? 5 : 3}
                  filter={isHov ? `url(#${filtId})` : undefined}
                  style={{
                    transform: isHov ? "scale(1.09)" : "scale(1)",
                    transformOrigin: `${p.x}px ${p.y}px`,
                    transition: "stroke 0.2s, transform 0.2s",
                  }}
                />

                {/* Labels */}
               {lines.map((line, li) => {
  // Define your line spacing in pixels
  const lineHeight = 22; 
  
  return (
    <text
      key={li}
      x={p.x}
      // Multiply the index by the line height
      y={textY + (li * lineHeight)} 
      textAnchor="middle"
      dominantBaseline="middle"
      fill={isHov ? "#fff" : "#ddd"}
      fontSize={isHov ? "19" : "18"}
      fontWeight={isHov ? 700 : 400}
      style={{ pointerEvents: "none" }}
    >
      {line}
    </text>
  );
})}
              </g>
            );
          })}

             {/* ── Category hub nodes (overview mode only — click to focus) ── */}
          {!loading && !focusedCat && catNodes.map(({ cat, x, y }) => {
            const p    = animPos(x, y);
            const isHov = hovCat === cat;
            const cfg  = getCat(cat);
            const filtId = `glow-${cat.replace(/\s+/g,"-")}`;
            const lines  = wrapWords(cat, 14);
            const lineH  = 14;
            const textY  = p.y - ((lines.length - 1) * lineH) / 2 + 1;

            return (
              <g key={`hub-${cat}`}
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHovCat(cat)}
                onMouseLeave={() => setHovCat(null)}
                onClick={() => setFocusedCat(cat)}
              >
                <circle cx={p.x + 4} cy={p.y + 5} r={CAT_HUB_R}
                  fill="#00000066" filter="url(#shadow3d)"/>
                <circle cx={p.x} cy={p.y} r={CAT_HUB_R}
                  fill={cfg.fill}
                  stroke={cfg.stroke}
                  strokeWidth={isHov ? 5 : 3.5}
                  filter={isHov ? `url(#${filtId})` : undefined}
                />
                {lines.map((line, li) => (
                  <text key={li}
                    x={p.x} y={textY + li * lineH}
                    textAnchor="middle" dominantBaseline="middle"
                    fill={cfg.color}
                    fontSize="18"
                    fontWeight="700"
                    style={{ pointerEvents: "none" }}
                  >
                    {line}
                  </text>
                ))}
              </g>
            );
          })}

             {/* ── Centre node — becomes a "back to overview" button in focus mode ── */}
          {!loading && (
            <g
              style={{ cursor: focusedCat ? "pointer" : "default" }}
              onClick={() => focusedCat && setFocusedCat(null)}
            >
              <circle cx={CX + 7} cy={CY + 9} r={CENTER_R}
                fill="#00000066" filter="url(#shadow3d)"/>
              <circle cx={CX} cy={CY} r={CENTER_R}
                fill="url(#centreGrad)"
                stroke={focusedCat ? getCat(focusedCat).color : "#ffe38a"}
                strokeWidth="5.5"
                filter="url(#glow-centre)"
              />
              {wrapWords(focusedCat ? `← ${subject}` : subject, 13).map((line, li, arr) => (
                <text key={li}
                  x={CX}
                  y={CY - ((arr.length - 1) * 16) / 2 + li * 16 + 1}
                  textAnchor="middle" dominantBaseline="middle"
                  fill="#fff"
                  fontSize={focusedCat ? "18" : "22"}
                  fontWeight="700"
                  style={{ pointerEvents: "none" }}
                >
                  {line}
                </text>
              ))}
            </g>
          )}

          {/* ── Loading placeholder ── */}
          {loading && (
            <text x={CX} y={CY} textAnchor="middle" dominantBaseline="middle"
              fill="#888" fontSize="22">
              Loading…
            </text>
          )}
        </svg>


      </div>
    </div>
  );
}
